import { useAuth } from '../contexts/AuthContext'
import { MatterType, Matter, Task } from './matterService'
import { API_BASE_URL } from '../config'

export interface ClientPayerSource {
  sourceType: 'bank-account' | 'credit-card';
  bankAccountName?: string;
  bankAccountBsb?: string;
  bankAccountNumber?: string;
}

export type ClientStatus = 'submitted' | 'review' | 'approved' | 'processing' | 'completed';

export const CLIENT_STATUSES: ClientStatus[] = ['submitted', 'review', 'approved', 'processing', 'completed'];

export interface Client {
  id: string;
  tenantId: string;
  createdAt: string;
  payerId: string;
  status: ClientStatus;
  firstName: string;
  lastName: string;
  fullName?: string;
  emailAddress?: string;
  mobileNumber?: string;
  companyName?: string;
  streetAddress?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  country?: string;
  source?: ClientPayerSource;
  [key: string]: unknown;
}

export interface ClientMatterFields {
  matterType: MatterType;
  propertyAddress: string;
  settlementDate?: string;
}

export interface CreateClientPayload {
  firstName: string;
  lastName: string;
  fullName?: string;
  emailAddress: string;
  mobileNumber: string;
  companyName?: string;
  streetAddress: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
  source: ClientPayerSource;
  matter?: ClientMatterFields;
}

export interface CreateClientResult {
  client: Client;
  matter?: Matter;
  tasks?: Task[];
  matterError?: string;
}

export const useClientService = () => {
  const { token, authFetch } = useAuth()

  const listClients = async (): Promise<Client[]> => {
    if (!token) {
      return []
    }

    const response = await authFetch(`${API_BASE_URL}/clients`)

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to list clients: ${response.status}`)
    }

    return data.clients || []
  }

  const createClient = async (payload: CreateClientPayload): Promise<CreateClientResult> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await authFetch(`${API_BASE_URL}/clients`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to create client: ${response.status}`)
    }

    return data
  }

  const updateClientStatus = async (clientId: string, status: ClientStatus): Promise<Client> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await authFetch(`${API_BASE_URL}/clients/${clientId}/status`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ status }),
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to update client status: ${response.status}`)
    }

    return data.client
  }

  return { listClients, createClient, updateClientStatus }
}
